/**
 * Email capture timing and focus handling.
 *
 * Opens once per visitor per configured window, on a delay or on exit intent,
 * whichever comes first. Never opens again once someone has signed up, and
 * never on the cart or account pages where it would be in the way.
 */

const STORAGE_KEY = 'midas:popup';

class MidasPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.popup__panel');
    this.form = this.querySelector('form');

    // A successful signup re-renders the page with the form posted — show the
    // confirmation immediately, and never ask again.
    //
    // This has to run before the suppression check, not after: submitting
    // stores 'subscribed', so on the redirect that follows, suppression is
    // already true and an early return here meant nobody ever saw the
    // confirmation or the code it carries.
    if (this.querySelector('.popup__message:not(.popup__message--error)')) {
      this.remember('subscribed');
      this.open();
      this.bindClose();
      return;
    }

    if (this.suppressed) return;

    this.timer = setTimeout(() => this.open(), Number(this.dataset.delay || 12) * 1000);

    if (this.hasAttribute('data-exit-intent')) {
      this.onExit = (event) => {
        if (event.clientY > 0) return;
        this.open();
      };
      document.addEventListener('mouseout', this.onExit);
    }

    this.bindClose();
    this.form?.addEventListener('submit', () => this.remember('subscribed'));
  }

  bindClose() {
    this.querySelectorAll('[data-popup-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.close();
      if (event.key === 'Tab') this.trap(event);
    };

    // Tapping a code on a phone is easier than selecting it
    this.querySelector('[data-copy-code]')?.addEventListener('click', (event) => {
      const button = event.currentTarget;
      const code = button.dataset.copyCode;
      navigator.clipboard?.writeText(code).then(
        () => {
          button.dataset.copied = 'true';
          setTimeout(() => delete button.dataset.copied, 2400);
        },
        () => {
          // Clipboard blocked — the code is on screen anyway
        }
      );
    });
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('mouseout', this.onExit);
    document.removeEventListener('keydown', this.onKeydown);
  }

  get suppressed() {
    const path = window.location.pathname;
    if (/\/(cart|account|challenge|checkouts)/.test(path)) return true;

    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved) return false;
      if (saved.state === 'subscribed') return true;
      return Date.now() < saved.until;
    } catch {
      return false;
    }
  }

  remember(state) {
    const days = Number(this.dataset.days || 30);
    try {
      // open() records 'seen' on the way out, which would otherwise downgrade
      // someone who just subscribed and ask them again in a month.
      if (state === 'seen') {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved?.state === 'subscribed') return;
      }
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ state, until: Date.now() + days * 24 * 60 * 60 * 1000 })
      );
    } catch {
      // Private browsing — the popup simply reappears next visit
    }
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    clearTimeout(this.timer);
    document.removeEventListener('mouseout', this.onExit);

    this.hidden = false;
    void this.offsetHeight;
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keydown', this.onKeydown);

    this.returnFocus = document.activeElement;
    (this.querySelector('input[type="email"]') || this.panel)?.focus({ preventScroll: true });

    this.remember('seen');
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keydown', this.onKeydown);
    setTimeout(() => {
      this.hidden = true;
    }, 400);
    this.returnFocus?.focus?.();
  }

  trap(event) {
    const focusable = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

customElements.define('midas-popup', MidasPopup);
